import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { finalize, map, of, switchMap, throwError } from 'rxjs';
import { AhmedElshahatJobService } from '../../../../services/ahmed-elshahat-job';
import { CompaniesService } from '../../../../services/companies';
import { CountryCityService } from '../../../../services/country-city.service';
import { LoginService } from '../../../../services/login.service';
import { ResigteringService } from '../../../../services/resigtering.service';

type CreateJobStep = 'jobTitle' | 'jobDetails' | 'account';


@Component({
  selector: 'app-create-job',
  templateUrl: './create-job.component.html',
  styleUrl: './create-job.component.scss'
})
export class CreateJobComponent implements OnInit {
  data!: FormGroup;
  accountForm!: FormGroup;

  contentReady = false;
  isSubmitting = false;
  currentStep: CreateJobStep = 'jobTitle';
  readonly jobSteps: CreateJobStep[] = ['jobTitle', 'jobDetails'];
  readonly guestSteps: CreateJobStep[] = [...this.jobSteps, 'account'];

  countries: any[] = [];
  cities: any[] = [];

  skills: any[] = [];
  filteredSkills: any[] = [];

  selectedSkillCategory = 'technical';

  errorMessage = '';
  successMessage = '';
  validationErrors: string[] = [];

  private readonly successRoute = '/jobs';

  skillCategories = [
    { value: 'education', labelKey: 'atw.createJob.skillCategories.education' },
    { value: 'experience', labelKey: 'atw.createJob.skillCategories.experience' },
    { value: 'certificate', labelKey: 'atw.createJob.skillCategories.certificate' },
    { value: 'language', labelKey: 'atw.createJob.skillCategories.language' },
    { value: 'technical', labelKey: 'atw.createJob.skillCategories.technical' },
    { value: 'soft_skill', labelKey: 'atw.createJob.skillCategories.softSkill' },
    { value: 'other', labelKey: 'atw.createJob.skillCategories.other' }
  ];

  requirementTypes = [
    { value: 'education', labelKey: 'atw.createJob.requirementTypes.education' },
    { value: 'experience', labelKey: 'atw.createJob.requirementTypes.experience' },
    { value: 'certificate', labelKey: 'atw.createJob.requirementTypes.certificate' },
    { value: 'language', labelKey: 'atw.createJob.requirementTypes.language' },
    { value: 'other', labelKey: 'atw.createJob.requirementTypes.other' }
  ];

  workModels = [
    { value: 'on_site', labelKey: 'atw.createJob.workModels.onSite' },
    { value: 'remote', labelKey: 'atw.createJob.workModels.remote' },
    { value: 'hybrid', labelKey: 'atw.createJob.workModels.hybrid' }
  ];

  employmentTypes = [
    { value: 'full_time', labelKey: 'atw.createJob.employmentTypes.fullTime' },
    { value: 'part_time', labelKey: 'atw.createJob.employmentTypes.partTime' },
    { value: 'contract', labelKey: 'atw.createJob.employmentTypes.contract' },
    { value: 'internship', labelKey: 'atw.createJob.employmentTypes.internship' }
  ];

  jobTypes = [
    { value: 'any', labelEn: 'All applicants', labelAr: 'كل المتقدمين' },
    { value: 'fordoctors', labelEn: 'Doctors only', labelAr: 'للأطباء فقط' },
    { value: 'fordentist', labelEn: 'Dentists only', labelAr: 'لأطباء الأسنان فقط' }
  ];

  salaryTypes = [
    { value: 'competitive', labelKey: 'atw.createJob.salaryTypes.competitive' },
    { value: 'negotiable', labelKey: 'atw.createJob.salaryTypes.negotiable' },
    { value: 'range', labelKey: 'atw.createJob.salaryTypes.range' },
    { value: 'hidden', labelKey: 'atw.createJob.salaryTypes.hidden' }
  ];

  currencies = ['SAR', 'AED', 'EGP', 'USD', 'EUR'];

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private transloco: TranslocoService,
    private jobService: AhmedElshahatJobService,
    private countryCityService: CountryCityService,
    private companiesService: CompaniesService,
    private resigteringService: ResigteringService,
    private loginService: LoginService
  ) {}

  ngOnInit(): void {
    this.createForm();
    this.createAccountForm();
    this.addRequirement();

    this.getCountries();
    this.getSkills();

    this.contentReady = true;
  }

  get isAr(): boolean {
    return this.transloco.getActiveLang() === 'ar';
  }

  get requirements(): FormArray {
    return this.data.get('requirements') as FormArray;
  }

  get needsCompanyOnboarding(): boolean {
    return !this.getStoredCompanyId();
  }

  get activeSteps(): CreateJobStep[] {
    return this.needsCompanyOnboarding ? this.guestSteps : this.jobSteps;
  }

  get selectedSkills(): any[] {
    const selectedIds = this.data?.value?.skills || [];
    return this.skills.filter((skill: any) => selectedIds.includes(skill.id));
  }

  createForm(): void {
    this.data = this.fb.group({
      user_id: [this.getStoredUserId()],
      company_id: [
        this.getStoredCompanyId()
      ],

      title_ar: ['', Validators.required],
      title_en: ['', Validators.required],

      country_id: [null, Validators.required],
      city_id: [null, Validators.required],

      work_model: ['on_site', Validators.required],
      employment_type: ['full_time', Validators.required],
      job_type: ['any'],

      description: ['', Validators.required],
      responsibilities: [''],

      salary_type: ['competitive'],
      salary_min: [null],
      salary_max: [null],
      salary_currency: ['SAR'],
      salary_currency_ar: ['', Validators.maxLength(50)],
      salary_note: [''],

      exam_required: [false],
      personality_assessment_required: [false],

      skills: [[]],
      requirements: this.fb.array([])
    });
  }

  createAccountForm(): void {
    this.accountForm = this.fb.group({
      company_name: ['', Validators.required],
      company_name_ar: [''],
      phone: ['', Validators.required],
      email: ['', Validators.email],
      specialty: [''],
      industry: [''],
      address: [''],
      facebook: [''],
      linkedin: ['']
    });
  }

  private getStoredUserId(): string | null {
    if (!this.canUseLocalStorage()) {
      return null;
    }

    const directId =
      localStorage.getItem('user_id') ||
      localStorage.getItem('userId');

    if (directId) {
      return directId;
    }

    const user = this.getStoredUser();
    return user?.id || user?.user_id || user?.user?.id || null;
  }

  private getStoredCompanyId(): string | null {
    if (!this.canUseLocalStorage()) {
      return null;
    }

    const directId =
      localStorage.getItem('company_id') ||
      localStorage.getItem('companyId');

    if (directId) {
      return directId;
    }

    const user = this.getStoredUser();
    return user?.company_id || user?.company?.id || user?.user?.company_id || null;
  }

  private getStoredUser(): any {
    if (!this.canUseLocalStorage()) {
      return null;
    }

    const storedUser =
      localStorage.getItem('user') ||
      localStorage.getItem('currentUser') ||
      localStorage.getItem('auth_user') ||
      localStorage.getItem('userData') ||
      localStorage.getItem('user_data');

    if (!storedUser) {
      return null;
    }

    try {
      return JSON.parse(storedUser);
    } catch {
      return null;
    }
  }

  private canUseLocalStorage(): boolean {
    return typeof localStorage !== 'undefined';
  }

  getCountries(): void {
    this.countryCityService.getCountries().subscribe({
      next: (res: any) => {
        this.countries = this.extractArray(res);
      },
      error: (err: any) => {
        console.log(err);
      }
    });
  }

  getCitiesByCountry(countryId: any, resetCity = true): void {
    if (!countryId) {
      this.cities = [];

      if (resetCity) {
        this.data.patchValue({ city_id: null });
      }

      return;
    }

    this.countryCityService.getCitiesByCountry(countryId).subscribe({
      next: (res: any) => {
        this.cities = this.extractArray(res);
      },
      error: (err: any) => {
        console.log(err);
      }
    });
  }

  onCountryChange(): void {
    const countryId = this.data.get('country_id')?.value;

    this.data.patchValue({
      city_id: null
    });

    this.getCitiesByCountry(countryId);
  }

  getSkills(): void {
    this.jobService.getAllSkills().subscribe({
      next: (res: any) => {
        this.skills = this.extractArray(res);
        this.filterSkillsByCategory();
      },
      error: (err: any) => {
        console.log(err);
      }
    });
  }

  selectSkillCategory(category: string): void {
    this.selectedSkillCategory = category;
    this.filterSkillsByCategory();
  }

  filterSkillsByCategory(): void {
    this.filteredSkills = this.skills.filter((skill: any) => {
      return skill.category === this.selectedSkillCategory;
    });
  }

  toggleSkill(skillId: any, event: Event): void {
    const input = event.target as HTMLInputElement;
    const selectedSkills = [...(this.data.value.skills || [])];

    if (input.checked) {
      if (!selectedSkills.includes(skillId)) {
        selectedSkills.push(skillId);
      }
    } else {
      const index = selectedSkills.indexOf(skillId);

      if (index > -1) {
        selectedSkills.splice(index, 1);
      }
    }

    this.data.patchValue({
      skills: selectedSkills
    });
  }

  isSkillSelected(skillId: any): boolean {
    return (this.data.value.skills || []).includes(skillId);
  }

  addRequirement(item: any = null): void {
    const requirementGroup = this.fb.group({
      requirement_text: [item?.requirement_text || ''],
      requirement_type: [item?.requirement_type || 'other'],
      is_required: [item?.is_required ?? true]
    });

    this.requirements.push(requirementGroup);
  }

  removeRequirement(index: number): void {
    if (this.requirements.length === 1) {
      return;
    }

    this.requirements.removeAt(index);
  }

  isInvalid(controlName: string): boolean {
    const control = this.data.get(controlName);
    return !!control && control.invalid && (control.touched || control.dirty);
  }

  isAccountInvalid(controlName: string): boolean {
    const control = this.accountForm.get(controlName);
    return !!control && control.invalid && (control.touched || control.dirty);
  }

  getStepLabel(step: CreateJobStep): string {
    const labels: Record<CreateJobStep, string> = {
      jobTitle: this.isAr ? 'بيانات الوظيفة' : 'Job information',
      jobDetails: this.isAr ? 'تفاصيل الوظيفة' : 'Job details',
      account: this.isAr ? 'بيانات الحساب' : 'Account details'
    };

    return labels[step];
  }

  isStepActive(step: CreateJobStep): boolean {
    return this.currentStep === step;
  }

  isStepDone(step: CreateJobStep): boolean {
    return this.activeSteps.indexOf(step) < this.activeSteps.indexOf(this.currentStep);
  }

  canGoBack(): boolean {
    return this.activeSteps.indexOf(this.currentStep) > 0;
  }

  goToStep(step: CreateJobStep): void {
    const targetIndex = this.activeSteps.indexOf(step);
    const currentIndex = this.activeSteps.indexOf(this.currentStep);

    if (targetIndex > -1 && targetIndex <= currentIndex) {
      this.currentStep = step;
      this.errorMessage = '';
      this.validationErrors = [];
    }
  }

  goBack(): void {
    const index = this.activeSteps.indexOf(this.currentStep);

    if (index > 0) {
      this.currentStep = this.activeSteps[index - 1];
      this.errorMessage = '';
      this.validationErrors = [];
    }
  }

  handleSubmit(): void {
    const currentIndex = this.activeSteps.indexOf(this.currentStep);
    const isLastStep = currentIndex === this.activeSteps.length - 1;

    if (!isLastStep) {
      this.goNext();
      return;
    }

    this.onSubmit();
  }

  goNext(): void {
    if (!this.validateCurrentStep()) {
      return;
    }

    const index = this.activeSteps.indexOf(this.currentStep);
    const nextStep = this.activeSteps[index + 1];

    if (nextStep) {
      this.currentStep = nextStep;
      this.errorMessage = '';
      this.validationErrors = [];
    }
  }

  validateCurrentStep(): boolean {
    if (this.currentStep === 'jobTitle') {
      return this.validateJobControls([
        'title_ar',
        'title_en',
        'country_id',
        'city_id',
        'work_model',
        'employment_type'
      ]);
    }

    if (this.currentStep === 'jobDetails') {
      return this.validateJobControls(['description']);
    }

    if (this.currentStep === 'account') {
      if (this.accountForm.invalid) {
        this.accountForm.markAllAsTouched();
        this.setValidationErrors(this.collectAccountValidationErrors());
        return false;
      }

      return true;
    }

    return true;
  }

  private validateJobControls(controlNames: string[]): boolean {
    let isValid = true;

    controlNames.forEach((controlName) => {
      const control = this.data.get(controlName);

      if (!control) {
        return;
      }

      control.markAsTouched();
      control.updateValueAndValidity();

      if (control.invalid) {
        isValid = false;
      }
    });

    if (!isValid) {
      this.setValidationErrors(this.collectJobValidationErrors(controlNames));
    }

    return isValid;
  }

  getPrimaryActionLabel(): string {
    if (this.isSubmitting) {
      return this.transloco.translate('atw.createJob.submitting');
    }

    const currentIndex = this.activeSteps.indexOf(this.currentStep);

    if (currentIndex < this.activeSteps.length - 1) {
      return this.isAr ? 'التالي' : 'Next';
    }

    return this.transloco.translate('atw.createJob.submit');
  }

  getSkillName(skill: any): string {
    if (!skill) {
      return '';
    }

    return this.isAr
      ? (skill.name_ar || skill.name_en || skill.name || '')
      : (skill.name_en || skill.name_ar || skill.name || '');
  }

  getCountryName(country: any): string {
    if (!country) {
      return '';
    }

    return this.isAr
      ? (country.country_name_ar || country.name_ar || country.country_name_en || country.name || '')
      : (country.country_name_en || country.name_en || country.country_name_ar || country.name || '');
  }

  getCityName(city: any): string {
    if (!city) {
      return '';
    }

    return this.isAr
      ? (city.city_name_ar || city.name_ar || city.city_name_en || city.name || '')
      : (city.city_name_en || city.name_en || city.city_name_ar || city.name || '');
  }

  emptyToNull(value: any): any {
    if (value === '' || value === undefined) {
      return null;
    }

    return value;
  }

  getCurrentDateTimeLocal(): string {
    const now = new Date();

    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  onSubmit(): void {
    this.errorMessage = '';
    this.successMessage = '';
    this.validationErrors = [];

    if (!this.validateCurrentStep()) {
      return;
    }

    if (this.data.invalid) {
      this.data.markAllAsTouched();
      this.currentStep = this.getFirstInvalidJobStep();
      this.setValidationErrors(this.collectAllValidationErrors());
      return;
    }

    if (this.needsCompanyOnboarding) {
      this.createCompanyAccountAndJob();
      return;
    }

    this.isSubmitting = true;

    const formValue = this.data.value;
    const companyId = formValue.company_id || this.getStoredCompanyId();

    if (!companyId) {
      this.isSubmitting = false;
      this.errorMessage = this.isAr
        ? 'لم يتم العثور على رقم الشركة. سجّل الدخول كشركة ثم حاول مرة أخرى.'
        : 'Company ID was not found. Please sign in as a company and try again.';
      return;
    }

    this.data.patchValue({ company_id: companyId }, { emitEvent: false });

    const payload = this.buildJobPayload(companyId, formValue.user_id || this.getStoredUserId());

    this.jobService.addJob(payload).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.successMessage = this.transloco.translate('atw.createJob.success');
        this.router.navigate([this.successRoute]);
      },
      error: (err: any) => {
        console.log(err);

        this.isSubmitting = false;
        this.errorMessage =
          this.getApiErrorMessage(err) ||
          this.transloco.translate('atw.createJob.submitError');
      }
    });
  }

  private createCompanyAccountAndJob(): void {
    if (this.data.invalid || this.accountForm.invalid) {
      this.data.markAllAsTouched();
      this.accountForm.markAllAsTouched();
      this.currentStep = this.data.invalid
        ? this.getFirstInvalidJobStep()
        : 'account';
      this.setValidationErrors(this.collectAllValidationErrors());
      return;
    }

    const accountValue = this.accountForm.value;
    const mobile = String(accountValue.phone || '').trim();
    let createdUserId: number | string | null = null;
    let createdCompanyId: number | string | null = null;

    this.isSubmitting = true;

    this.resigteringService
      .user_Resigter(this.buildRegisterPayload(accountValue, mobile))
      .pipe(
        switchMap((registerResponse: any) => {
          createdUserId = this.extractUserId(registerResponse);
          return this.companiesService.create(
            this.buildCompanyFormData(accountValue, accountValue, createdUserId)
          );
        }),
        switchMap((companyResponse: any) => {
          createdCompanyId = this.extractCompanyId(companyResponse);

          if (!createdCompanyId) {
            return throwError(() => new Error(
              this.isAr ? 'لم يتم إنشاء الشركة بشكل صحيح' : 'Company was not created correctly'
            ));
          }

          return this.loginService.user_login({
            username: mobile,
            password: mobile
          });
        }),
        switchMap((loginResponse: any) => {
          if (!loginResponse?.token) {
            return throwError(() => new Error(
              this.isAr ? 'تم إنشاء الحساب لكن تسجيل الدخول التلقائي لم يكتمل' : 'Account created but auto login failed'
            ));
          }

          this.saveLoginSession(loginResponse, createdCompanyId);

          if (createdUserId && createdCompanyId) {
            return this.resigteringService
              .editUser(
                createdUserId,
                this.buildCompanyUserUpdateFormData(createdCompanyId, accountValue, mobile)
              )
              .pipe(
                map((updateResponse: any) => ({
                  ...loginResponse,
                  user: {
                    ...(loginResponse.user || {}),
                    ...(updateResponse?.user || {}),
                    company_id: createdCompanyId,
                    user_type: 'company'
                  }
                }))
              );
          }

          return of(loginResponse);
        }),
        switchMap((loginResponse: any) => {
          this.saveLoginSession(loginResponse, createdCompanyId);

          const userId = loginResponse?.user?.id || createdUserId;

          if (!createdCompanyId) {
            return throwError(() => new Error(
              this.isAr ? 'لم يتم العثور على رقم الشركة' : 'Company ID was not found'
            ));
          }

          return this.jobService.addJob(this.buildJobPayload(createdCompanyId, userId));
        }),
        finalize(() => {
          this.isSubmitting = false;
        })
      )
      .subscribe({
        next: () => {
          this.successMessage = this.transloco.translate('atw.createJob.success');
          this.router.navigate([this.successRoute]);
        },
        error: (err: any) => {
          console.log(err);
          this.validationErrors = [];
          this.errorMessage = this.getApiErrorMessage(err);
        }
      });
  }

  private setValidationErrors(errors: string[]): void {
    this.validationErrors = errors;
    this.errorMessage = this.transloco.translate('atw.createJob.formError');
  }

  private collectAllValidationErrors(): string[] {
    return [
      ...this.collectJobValidationErrors([
        'title_ar',
        'title_en',
        'country_id',
        'city_id',
        'work_model',
        'employment_type',
        'description'
      ]),
      ...(this.needsCompanyOnboarding ? this.collectAccountValidationErrors() : [])
    ];
  }

  private collectJobValidationErrors(controlNames: string[]): string[] {
    return controlNames
      .map((controlName) => this.getControlErrorMessage(this.data, controlName))
      .filter((message): message is string => !!message);
  }

  private collectAccountValidationErrors(): string[] {
    return [
      'company_name',
      'phone',
      'email'
    ]
      .map((controlName) => this.getControlErrorMessage(this.accountForm, controlName))
      .filter((message): message is string => !!message);
  }

  private getControlErrorMessage(form: FormGroup, controlName: string): string | null {
    const control = form.get(controlName);

    if (!control || control.valid) {
      return null;
    }

    const label = this.getFieldLabel(controlName);

    if (control.hasError('required')) {
      return this.isAr
        ? `${label}: هذا الحقل مطلوب.`
        : `${label}: This field is required.`;
    }

    if (control.hasError('email')) {
      return this.isAr
        ? `${label}: البريد الإلكتروني غير صحيح.`
        : `${label}: Invalid email address.`;
    }

    return this.isAr
      ? `${label}: القيمة غير صحيحة.`
      : `${label}: Invalid value.`;
  }

  private getFieldLabel(controlName: string): string {
    const arLabels: Record<string, string> = {
      title_ar: 'عنوان الوظيفة بالعربية',
      title_en: 'عنوان الوظيفة بالإنجليزية',
      country_id: 'الدولة',
      city_id: 'المدينة',
      work_model: 'نظام العمل',
      employment_type: 'نوع التوظيف',
      description: 'وصف الوظيفة',
      company_name: 'اسم الشركة',
      phone: 'رقم الموبايل',
      email: 'البريد الإلكتروني'
    };

    const enLabels: Record<string, string> = {
      title_ar: 'Arabic job title',
      title_en: 'English job title',
      country_id: 'Country',
      city_id: 'City',
      work_model: 'Work model',
      employment_type: 'Employment type',
      description: 'Job description',
      company_name: 'Company name',
      phone: 'Mobile number',
      email: 'Email'
    };

    return (this.isAr ? arLabels : enLabels)[controlName] || controlName;
  }

  private buildJobPayload(companyId: any, userId: any): any {
    const formValue = this.data.value;

    return {
      user_id: this.emptyToNull(userId || formValue.user_id),
      company_id: this.emptyToNull(companyId),

      doctor_id: null,
      complex_request_id: null,

      title_ar: formValue.title_ar?.trim(),
      title_en: formValue.title_en?.trim(),

      country_id: this.emptyToNull(formValue.country_id),
      city_id: this.emptyToNull(formValue.city_id),

      work_model: formValue.work_model,
      employment_type: formValue.employment_type,
      job_type: formValue.job_type || 'any',

      description: formValue.description?.trim(),
      responsibilities: formValue.responsibilities?.trim(),

      salary_type: formValue.salary_type,
      salary_min: this.emptyToNull(formValue.salary_min),
      salary_max: this.emptyToNull(formValue.salary_max),
      salary_currency: formValue.salary_currency,
      salary_currency_ar: this.emptyToNull(formValue.salary_currency_ar?.trim()),
      salary_note: formValue.salary_note?.trim(),

      exam_required: formValue.exam_required ? 1 : 0,
      personality_assessment_required: formValue.personality_assessment_required ? 1 : 0,

      status: 'published',
      posted_at: this.getCurrentDateTimeLocal(),

      skills: formValue.skills || [],

      requirements: (formValue.requirements || [])
        .filter((req: any) => req.requirement_text?.trim())
        .map((req: any) => ({
          requirement_text: req.requirement_text.trim(),
          requirement_type: req.requirement_type,
          is_required: req.is_required ? 1 : 0
        }))
    };
  }

  private getFirstInvalidJobStep(): CreateJobStep {
    const firstStepControls = [
      'title_ar',
      'title_en',
      'country_id',
      'city_id',
      'work_model',
      'employment_type'
    ];

    if (firstStepControls.some((controlName) => this.data.get(controlName)?.invalid)) {
      return 'jobTitle';
    }

    return 'jobDetails';
  }

  private buildRegisterPayload(value: any, mobile: string): FormData {
    const cleanMobile = mobile.replace(/\D/g, '') || mobile;
    const formData = new FormData();

    formData.append('username', mobile);
    formData.append('type', '9');
    formData.append('user_type', 'company');
    formData.append('role', 'company');
    formData.append('first_name', String(value.company_name || '').trim());
    formData.append('last_name', String(value.company_name_ar || value.company_name || '').trim());
    formData.append('email', value.email || `${cleanMobile}@company.local`);
    formData.append('phone', mobile);
    formData.append('mobile', mobile);
    formData.append('tel', mobile);
    formData.append('password', mobile);
    formData.append('password_confirmation', mobile);
    formData.append('company_id', '');
    formData.append('doctor_id', '');
    formData.append('is_active', '1');

    return formData;
  }

  private buildCompanyFormData(accountValue: any, companyValue: any, userId: any): FormData {
    const formData = new FormData();
    const jobValue = this.data.value;
    const normalFields: Record<string, any> = {
      name: accountValue.company_name,
      name_ar: accountValue.company_name_ar || accountValue.company_name,
      specialty: companyValue.specialty,
      industry: companyValue.industry,
      tel: accountValue.phone,
      address: companyValue.address,
      contact_email: accountValue.email,
      facebook: companyValue.facebook,
      linkedin: companyValue.linkedin
    };

    Object.entries(normalFields).forEach(([key, value]) => {
      if (value !== null && value !== undefined && String(value).trim() !== '') {
        formData.append(key, String(value).trim());
      }
    });

    if (jobValue.country_id) {
      formData.append('country_id', String(jobValue.country_id));
    }

    if (jobValue.city_id) {
      formData.append('city_id', String(jobValue.city_id));
      formData.append('cities_id', String(jobValue.city_id));
    }

    if (userId) {
      formData.append('user_id', String(userId));
    }

    formData.append('is_user', '1');
    formData.append('is_ahmed_elshahat_client', '0');
    formData.append('add_by', 'user');

    return formData;
  }

  private buildCompanyUserUpdateFormData(
    companyId: number | string,
    value: any,
    mobile: string
  ): FormData {
    const cleanMobile = mobile.replace(/\D/g, '') || mobile;
    const formData = new FormData();

    formData.append('username', mobile);
    formData.append('first_name', String(value.company_name || '').trim());
    formData.append('last_name', String(value.company_name_ar || value.company_name || '').trim());
    formData.append('phone', mobile);
    formData.append('email', value.email || `${cleanMobile}@company.local`);
    formData.append('company_id', String(companyId));
    formData.append('doctor_id', '');
    formData.append('user_type', 'company');
    formData.append('role', 'company');
    formData.append('type', '9');

    return formData;
  }

  private saveLoginSession(res: any, fallbackCompanyId?: number | string | null): void {
    if (!res?.token) {
      return;
    }

    if (this.canUseLocalStorage()) {
      const companyId = res.user?.company_id || fallbackCompanyId;

      if (res.user?.username) {
        localStorage.setItem('username', res.user.username);
      }

      if (res.user?.id !== undefined && res.user?.id !== null) {
        localStorage.setItem('user_id', String(res.user.id));
      }

      localStorage.setItem('user_type', res.user?.user_type || 'company');
      localStorage.setItem('type', String(res.user?.type ?? 9));

      if (companyId !== undefined && companyId !== null) {
        localStorage.setItem('company_id', String(companyId));
      }

      localStorage.setItem(
        'user',
        JSON.stringify({
          ...(res.user || {}),
          user_type: res.user?.user_type || 'company',
          type: res.user?.type ?? 9,
          company_id: companyId || null
        })
      );
    }

    this.loginService.setToken(res.token);
  }

  private extractUserId(response: any): number | string | null {
    return (
      response?.data?.id ||
      response?.data?.user?.id ||
      response?.user?.id ||
      response?.id ||
      null
    );
  }

  private extractCompanyId(response: any): number | string | null {
    return (
      response?.data?.id ||
      response?.data?.company?.id ||
      response?.data?.data?.id ||
      response?.company?.id ||
      response?.id ||
      null
    );
  }

  private getApiErrorMessage(error: any): string {
    if (error?.message) {
      return error.message;
    }

    if (error?.error?.errors) {
      const firstKey = Object.keys(error.error.errors)[0];
      return error.error.errors[firstKey][0];
    }

    return error?.error?.message || this.transloco.translate('atw.createJob.submitError');
  }

  extractArray(res: any): any[] {
    return res?.data?.data || res?.data || res || [];
  }

  trackById(index: number, item: any): any {
    return item?.id || index;
  }

  trackByIndex(index: number): number {
    return index;
  }
}
